import { useEffect, useCallback } from 'react';
import { dispatchHttpEditorFindIfFocused } from '@terra/ui-design-system';
import { useProxyStore } from '../../store/proxy-store';
import { useElectronProxy } from '../useElectronProxy';
import type { OpenMainContentTabInput } from '../../store/layoutStore';
import { requestBoothTourRestart } from '../../components/booth/booth-tour-restart';
import { useBoothHandoff } from '../useBoothHandoff';
import { useFeatureFlags } from '../useFeatureFlags';

/**
 * Registers Electron menu-command listeners that bridge the native
 * menu bar (start/stop proxy, settings, intercept, find) to the
 * React application state. Cleans up listeners on unmount.
 */
export function useElectronMenuCommands(
  navigateInActiveTab: (tab: OpenMainContentTabInput) => void,
) {
  const { startProxy, stopProxy, clearHistory } = useElectronProxy();
  const handoffNextPlayer = useBoothHandoff({
    onRestartTour: requestBoothTourRestart,
  });
  const {
    isFeatureFlagEnabled: boothTourEnabled,
    isFetched: isBoothTourFlagFetched,
  } = useFeatureFlags('BOOTH_TOUR_ENABLED');

  const handleStartProxy = useCallback(async () => {
    const { isProxyRunning, settings } = useProxyStore.getState();

    if (!isProxyRunning) {
      await startProxy(settings);
    }
  }, [startProxy]);

  const handleStopProxy = useCallback(async () => {
    if (useProxyStore.getState().isProxyRunning) {
      await stopProxy();
    }
  }, [stopProxy]);

  const handleClearHistory = useCallback(() => {
    clearHistory();
  }, [clearHistory]);

  const handleOpenSettings = useCallback(() => {
    navigateInActiveTab({
      id: 'settings',
      title: 'Target Settings',
      type: 'settings',
    });
  }, [navigateInActiveTab]);

  const handleInterceptToggle = useCallback(() => {
    const { settings, updateSettings } = useProxyStore.getState();

    updateSettings({ interceptEnabled: !settings.interceptEnabled });
  }, []);

  const handleFind = useCallback(() => {
    if (dispatchHttpEditorFindIfFocused(document.activeElement)) {
      return;
    }

    const searchInput = document.querySelector<HTMLInputElement>(
      'input[placeholder*="Search"]',
    );

    if (searchInput) {
      searchInput.focus();
      searchInput.select();
    }
  }, []);

  const handleBoothRestartTour = useCallback(() => {
    if (!isBoothTourFlagFetched || !boothTourEnabled) {
      return;
    }

    requestBoothTourRestart();
  }, [boothTourEnabled, isBoothTourFlagFetched]);

  const handleBoothResetStation = useCallback(() => {
    if (!isBoothTourFlagFetched || !boothTourEnabled) {
      return;
    }

    void handoffNextPlayer();
  }, [boothTourEnabled, isBoothTourFlagFetched, handoffNextPlayer]);

  useEffect(() => {
    if (!window.electronAPI) return;

    const handlers: [string, (...args: unknown[]) => void][] = [
      ['menu-start-proxy', handleStartProxy],
      ['menu-stop-proxy', handleStopProxy],
      ['menu-clear-history', handleClearHistory],
      ['menu-proxy-settings', handleOpenSettings],
      ['menu-intercept-toggle', handleInterceptToggle],
      ['menu-find', handleFind],
      ['menu-booth-restart-tour', handleBoothRestartTour],
      ['menu-booth-reset-station', handleBoothResetStation],
    ];

    for (const [event, handler] of handlers) {
      window.electronAPI.on(event, handler);
    }

    return () => {
      if (!window.electronAPI?.removeListener) return;
      for (const [event, handler] of handlers) {
        window.electronAPI.removeListener(event, handler);
      }
    };
  }, [
    handleStartProxy,
    handleStopProxy,
    handleClearHistory,
    handleOpenSettings,
    handleInterceptToggle,
    handleFind,
    handleBoothRestartTour,
    handleBoothResetStation,
  ]);
}
